import { useAtomValue } from 'jotai';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { bulkSyncPhaseAtom } from '@/atoms/chronicle';
import type { RootState } from '@/store';

const OTA_BATTERY_THRESHOLD = 20;

/**
 * Shared pre-update safety guards for firmware OTA.
 * Returns a human-readable reason string if the upgrade should be blocked, or null if clear.
 *
 * Soft gate only — Rust still hard-rejects on start (including an empty-device
 * file probe for idle/complete phases).
 */
export function useOtaGuards(batteryLevel: number | null): string | null {
  const intl = useIntl();
  const bulkSyncPhase = useAtomValue(bulkSyncPhaseAtom);
  const isRecording = useSelector((state: RootState) => state.timerModel.isRunning);

  const isBulkSyncActive =
    bulkSyncPhase === 'downloading' || bulkSyncPhase === 'uploading';
  const hasUnsyncedBulkSyncState =
    bulkSyncPhase === 'interrupted' || bulkSyncPhase === 'error';
  // `null` = battery not yet read; a genuine 0% must still block.
  const isBatteryTooLow = batteryLevel !== null && batteryLevel < OTA_BATTERY_THRESHOLD;

  if (isRecording)
    return intl.formatMessage({
      defaultMessage: 'Please end recording before updating firmware',
      id: 'NOYrKULeMk',
    });
  if (isBulkSyncActive)
    return intl.formatMessage({
      defaultMessage: 'Please wait for sync to complete before updating firmware',
      id: 'B4SR3o7WgU',
    });
  if (hasUnsyncedBulkSyncState)
    return intl.formatMessage({
      defaultMessage:
        'Unsynced sessions are still on your device. Sync them before upgrading.',
      id: 'bDAao/owUT',
    });
  if (isBatteryTooLow)
    return intl.formatMessage(
      {
        defaultMessage: 'Battery must be at least {threshold}% to update firmware',
        id: 'DFYEsBS9Qg',
      },
      { threshold: OTA_BATTERY_THRESHOLD }
    );

  return null;
}
